import { memo, useCallback, useState } from 'react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components';
import {
  Button,
  Error,
  Field,
  Modal,
  SuccessStatus,
} from '@/components/generic';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRegisterPhysicalCard } from '@package/smart-wallet/hooks';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const RegisterPhysicalCardSchema = z.object({
  cardTypeId: z
    .number({ required_error: 'Card type ID is required' })
    .int()
    .positive('Card type ID must be a positive number'),
  cardNumber: z
    .string({ required_error: 'Card number is required' })
    .min(1, 'Card number is required')
    .regex(/^\d+$/, 'Card number must contain only digits'),
  tierId: z
    .number()
    .int()
    .positive('Tier ID must be a positive number')
    .optional(),
  amount: z
    .number()
    .min(0, 'Amount must be a non-negative number')
    .optional(),
});

type RegisterPhysicalCardFormType = z.infer<typeof RegisterPhysicalCardSchema>;

type RegisterPhysicalCardModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const RegisterPhysicalCardModal = ({
  open,
  onClose,
  onSuccess,
}: RegisterPhysicalCardModalProps) => {
  const { t } = useTranslation();
  const [isSuccess, setIsSuccess] = useState(false);
  const {
    handleRegisterPhysicalCard,
    isLoading,
    error: hookError,
  } = useRegisterPhysicalCard();

  const form = useForm<RegisterPhysicalCardFormType>({
    resolver: zodResolver(RegisterPhysicalCardSchema),
    defaultValues: {
      cardTypeId: Number(import.meta.env.VITE_PHYSICAL_CARD_TYPE_ID),
      cardNumber: '',
      tierId: undefined,
      amount: undefined,
    },
  });

  const handleClose = useCallback(() => {
    if (!isLoading) {
      form.reset();
      setIsSuccess(false);
      onClose();
    }
  }, [isLoading, form, onClose]);

  const onSubmit = useCallback(
    async (data: RegisterPhysicalCardFormType) => {
      try {
        const tierId = import.meta.env.VITE_PHYSICAL_CARD_TIER_ID;
        await handleRegisterPhysicalCard(
          data.cardTypeId,
          data.cardNumber,
          tierId ? Number(tierId) : undefined,
          data.amount,
        );
        setIsSuccess(true);
        onSuccess();
      } catch {
        // Error handled by hook
      }
    },
    [handleRegisterPhysicalCard, onSuccess],
  );

  return (
    <Modal open={open} onClose={handleClose}>
      {isSuccess ? (
        <SuccessStatus
          title={t('cards.physical_card.register_success_title')}
          content={t('cards.physical_card.register_success_message')}
          onClose={handleClose}
          confirm={handleClose}
        />
      ) : (
        <>
          <div className='mb-4 text-h4 max-md:text-h5 text-t-primary'>
            {t('cards.physical_card.register_title')}
          </div>
          <p className='mb-6 text-body-2 text-t-secondary'>
            {t('cards.physical_card.register_description')}
          </p>
          <Form {...form}>
            <form
              className='flex flex-col gap-4'
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <FormField
                control={form.control}
                name='cardNumber'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Field
                        label={t('cards.physical_card.card_number')}
                        type='text'
                        placeholder='e.g. 1234567890123456'
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isLoading}
                        maxLength={19}
                      />
                    </FormControl>
                    <FormMessage className='text-primary-03/80' />
                  </FormItem>
                )}
              />

              {/*
              <FormField
                control={form.control}
                name='amount'
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Field
                        label={`${t('cards.physical_card.amount')} (${t('cards.physical_card.optional')})`}
                        type='number'
                        placeholder='e.g. 50.00'
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          field.onChange(val ? Number(val) : undefined);
                        }}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage className='text-primary-03/80' />
                  </FormItem>
                )}
              /> */}

              <Error
                error={hookError ? true : false}
                message={hookError || t('cards.physical_card.register_error')}
              />

              <div className='flex justify-end gap-3 mt-4'>
                <Button
                  className='flex-1'
                  isStroke
                  type='button'
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  {t('cards.physical_card.cancel')}
                </Button>
                <Button
                  className='flex-1'
                  isBlack={!isLoading}
                  isWhite={isLoading}
                  type='submit'
                  disabled={isLoading}
                >
                  {isLoading
                    ? t('cards.physical_card.registering')
                    : t('cards.physical_card.register')}
                </Button>
              </div>
            </form>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default memo(RegisterPhysicalCardModal);
